import { useState, useMemo, useEffect } from 'react';
import { MembershipDto, VenueDto } from '@/services/platform/api/model';
import type { WhereSheetProps } from '../types';
import { cn } from '@/lib/core/utils/cn';
import { useClientTranslation } from '@/lib/core/i18n';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import {
  RadioGroup,
  RadioGroupItem,
  RadioGroupLabel,
  RadioGroupSupportText,
} from '@/components/ui/radio-group';
import { BookHubs } from '@/components/shared/booking/common/book/hubs';
import { Icon } from '@/components/ui/icon';
import { useLocation } from 'react-router-dom';
import { VenueClosedSheet } from '../../shared/sheets/venueClosed';
import { useLocaleStore } from '@/lib/core/stores';
import { getSevenRoomsVenues } from '@/services/platform/data/booking/sevenrooms';
import { getMembership } from '@/services/platform/data/profile';

export function WhereSheet({ isOpen, onClose, cmsProps, booking, onBookingChange }: WhereSheetProps) {
  const t = useClientTranslation();
  const location = useLocation();
  const locale = useLocaleStore(s => s.locale);
  const categoryId = (location.state as { categoryId?: string } | null)?.categoryId;
  const targetVenueNodeKey = (location.state as { venueNodeKey?: string | null } | null)?.venueNodeKey;

  const [venues, setVenues] = useState<VenueDto[]>([]);
  const ALL_HUB = t('App.Labels.All');

  useEffect(() => {
    getSevenRoomsVenues({ serviceCategoryNodeKey: categoryId }, locale).then(result => {
      setVenues(result ?? []);
    });
  }, [categoryId, locale]);

  const [membership, setMembership] = useState<MembershipDto | undefined>(undefined);

  useEffect(() => {
    getMembership().then(setMembership);
  }, []);

  const isMember = membership?.isActive === true;

  const isVenueMemberOnly = (venue: VenueDto) => venue.membersOnly ?? false;
  const isMemberLocked = (venue: VenueDto) => isVenueMemberOnly(venue) && !isMember;
  const isClosed = (venue: VenueDto) => venue.isVenueClosed ?? false;
  const isDisabled = (venue: VenueDto) => isMemberLocked(venue) || isClosed(venue);

  const [closedVenue, setClosedVenue] = useState<VenueDto | null>(null);

  const commitSelection = (venue: VenueDto) => {
    onBookingChange({ venue, date: null, room: null, time: null });
    onClose();
  };

  useEffect(() => {
    if (!venues.length) return;
    const enabled = venues.filter(v => !isDisabled(v));

    // Keep the selection in sync with the venue the user arrived from.
    if (targetVenueNodeKey) {
      if (booking.venue?.venueNodeKey === targetVenueNodeKey) return;
      const preselected = enabled.find(v => v.venueNodeKey === targetVenueNodeKey);
      if (preselected) onBookingChange({ venue: preselected, date: null, room: null, time: null });
      return;
    }

    if (enabled.length !== 1) return;
    if (booking.venue?.venueNodeKey === enabled[0].venueNodeKey) return;
    onBookingChange({ venue: enabled[0], date: null, room: null, time: null });
    onClose();
  }, [venues, isMember, targetVenueNodeKey, booking.venue]);

  const hubs = useMemo(() => {
    const locations = venues.map(v => v.location).filter((l): l is string => !!l);
    return [ALL_HUB, ...Array.from(new Set(locations))];
  }, [venues]);

  const [activeHub, setActiveHub] = useState(ALL_HUB);

  const filtered = useMemo(
    () => (activeHub === ALL_HUB ? venues : venues.filter(v => v.location === activeHub)),
    [venues, activeHub]
  );

  return (
    <Drawer
      open={isOpen}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <DrawerContent className="h-[80dvh] min-h-[min(650px,100dvh)]">
        <div data-base-ui-swipe-ignore className="flex flex-col gap-8 overflow-y-auto px-4 pt-8 pb-25">
          <DrawerHeader className="gap-2 px-0">
            <div className="flex items-center gap-8">
              <DrawerTitle className="flex-1 text-[32px] font-medium tracking-[0.5px]">
                {cmsProps?.title ?? t('App.Labels.Where')}
              </DrawerTitle>
              <DrawerClose
                className="flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-full"
                aria-label="Close"
              >
                <Icon
                  name="xmark"
                  width={24}
                  height={24}
                  strokeWidth={1.5}
                  className="text-jncquoi-neutral-1000"
                />
              </DrawerClose>
            </div>
            {cmsProps?.subtitle && (
              <DrawerDescription className="text-[16px] text-[#707070]">
                {cmsProps.subtitle}
              </DrawerDescription>
            )}
          </DrawerHeader>

          <BookHubs hubs={hubs} activeHub={activeHub} onHubChange={setActiveHub} />

          {/* Venue list */}
          <RadioGroup
            value={booking.venue?.venueNodeKey ?? ''}
            onValueChange={key => {
              const venue = filtered.find(v => v.venueNodeKey === key);
              if (venue) commitSelection(venue);
            }}
            className="flex flex-col"
          >
            {filtered.map(venue => {
              const disabled = isDisabled(venue);
              const memberLocked = isMemberLocked(venue);
              const closed = !memberLocked && isClosed(venue);
              const itemId = `where-${venue.venueNodeKey}`;
              const isSelected = !!venue.venueNodeKey && venue.venueNodeKey === booking.venue?.venueNodeKey;

              return (
                <RadioGroupLabel
                  key={venue.venueNodeKey}
                  htmlFor={itemId}
                  className={cn(
                    'flex w-full cursor-pointer items-center gap-2 border-b-[0.5px] border-[#e1e1e1] py-[24px] text-[20px] tracking-normal text-[#1a1a1a]',
                    disabled && 'opacity-40',
                    memberLocked && 'pointer-events-none'
                  )}
                  // `onValueChange` only fires when the value actually changes, so re-tapping the
                  // venue that is already chosen would otherwise leave the sheet open. Confirming
                  // the existing answer has to close it too. `preventDefault` stops the label from
                  // forwarding the click to its radio, which would bubble back here and commit
                  // twice. Closed venues stay tappable so they can explain why and offer alternatives.
                  onClick={event => {
                    if (closed) {
                      event.preventDefault();
                      setClosedVenue(venue);
                      return;
                    }
                    if (disabled || !isSelected) return;
                    event.preventDefault();
                    commitSelection(venue);
                  }}
                >
                  {/* A disabled radio swallows its own taps, so let them fall through to the label. */}
                  <RadioGroupItem
                    id={itemId}
                    value={venue.venueNodeKey ?? ''}
                    disabled={disabled}
                    className={cn(closed && 'pointer-events-none')}
                  />

                  <div className="flex flex-col items-start gap-1">
                    <span className="font-pp-neue-montreal font-normal">{venue.name}</span>
                    {memberLocked && (
                      <RadioGroupSupportText className="rounded-[2px] bg-[#f4f4f4] px-0 py-0.5 text-[11px] font-medium tracking-[0.5px] text-[#9a9a9a] uppercase">
                        {t('App.Labels.MembersOnly')}
                      </RadioGroupSupportText>
                    )}
                  </div>
                </RadioGroupLabel>
              );
            })}
          </RadioGroup>
        </div>

        <VenueClosedSheet venue={closedVenue} onClose={() => setClosedVenue(null)} />
      </DrawerContent>
    </Drawer>
  );
}
